import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import zoomPlugin from 'chartjs-plugin-zoom';
import { historyZoomOptions } from './HistoryZoom';
import historyTooltip from './HistoryTooltip';
import { Line } from 'react-chartjs-2';
import { fade, makeStyles, useTheme } from '@material-ui/core/styles';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography, useMediaQuery } from '@material-ui/core';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import CloseIcon from '@material-ui/icons/Close';
import RestoreIcon from '@material-ui/icons/Restore';
import { useTranslation } from 'react-i18next';
import { databaseSeriesColor, formatDatabaseNumber, irrigationPhaseColors } from '../../Utils/database_history_utils';
import { dayNightCo2PreheatMapping, irrigationPhaseMapping } from '../../Constants/constants';
import { getLightProfileColour } from '../../Utils/light_profile_colours';
import { InfoButton } from '../../FormWidgets/Description';

const phaseColors = ['#e6b647', '#6379bd', '#6ba970', '#c68c58'];
const EMPTY = [];
const useStyles = makeStyles((theme) => ({
  canvas: { '& canvas': { touchAction: 'pan-y !important' } },
  expandedCanvas: { flex: '1 1 0', '& canvas': { touchAction: 'none !important' } },
  dialogTitle: { padding: theme.spacing(1) },
  dialogContent: { padding: theme.spacing(0, 1, 1), display: 'flex', flexDirection: 'column' }
}));

function axisNumber(value) {
  return new Intl.NumberFormat(undefined, Math.abs(value) >= 1000 ?
    { notation: 'compact', maximumFractionDigits: 1 } :
    value !== 0 && Math.abs(value) < 0.01 ? { notation: 'scientific', maximumFractionDigits: 1 } :
    { maximumFractionDigits: 2 }).format(value);
}

function timeTick(value, span) {
  const date = new Date(value);
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', ...(span < 300000 ? { second: '2-digit' } : {}) });
  const day = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return span <= 86400000 ? time : span <= 3 * 86400000 ? [day, time] : day;
}

// No extrapolation beyond the last observation, and no fill through unknown phases.
const background = {
  id: 'recordedPhases',
  beforeDraw(chart, _, { phases }) {
    const { ctx, chartArea: area, scales: { x } } = chart;
    if (!area) return;
    ctx.save();
    ctx.beginPath();
    ctx.rect(area.left, area.top, area.width, area.height);
    ctx.clip();
    for (let index = 0; index + 1 < phases.length; index++) {
      const point = phases[index], next = phases[index + 1];
      if (!phaseColors[point.y]) continue;
      ctx.fillStyle = phaseColors[point.y] + '28';
      const left = x.getPixelForValue(point.x), right = x.getPixelForValue(next.x);
      ctx.fillRect(left, area.top, right - left, area.height);
    }
    ctx.restore();
  }
};

const activityLanes = {
  id: 'recordedActivity',
  afterDraw(chart, _, { activities, color, divider, phaseTextColors }) {
    const { ctx, chartArea: area, scales: { x } } = chart;
    if (!area) return;
    ctx.save();
    ctx.font = '11px sans-serif';
    activities.forEach((activity, lane) => {
      const top = x.bottom + 10 + lane * 34;
      ctx.fillStyle = color;
      ctx.fillText(activity.label, area.left, top, area.width);
      const irrigationPhase = activity.kind === 'irrigation_phase';
      activity.points.forEach((point, index) => {
        if (!(irrigationPhase ? [0, 1, 2, 3] : [0, 1]).includes(point.y) || point.x > x.max) return;
        const next = activity.points[index + 1];
        if (point.x < x.min && (!next || next.x <= x.min || next.y === null)) return;
        ctx.fillStyle = irrigationPhase ? irrigationPhaseColors[point.y] : point.y === 1 ? databaseSeriesColor(activity.id) : divider;
        const left = x.getPixelForValue(Math.max(point.x, x.min));
        // An isolated active sample is a mark, never a fabricated complete run.
        const right = next && next.y !== null ? x.getPixelForValue(Math.min(next.x, x.max)) : left + 2;
        ctx.fillRect(left, top + 5, Math.max(2, right - left), irrigationPhase ? 16 : 9);
        if (irrigationPhase && right - left > 22) {
          ctx.fillStyle = phaseTextColors[point.y];
          ctx.fillText('P' + point.y, left + 4, top + 17, right - left - 4);
        }
      });
    });
    ctx.restore();
  }
};


// Observed minimum/maximum envelope, not an additional sensor trace.
const envelope = {
  id: 'measurementEnvelope',
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea: area } = chart;
    ctx.save(); ctx.beginPath(); ctx.rect(area.left, area.top, area.width, area.height); ctx.clip();
    chart.data.datasets.forEach((dataset, index) => {
      if (!chart.isDatasetVisible(index)) return;
      const x = chart.scales.x, y = chart.scales[dataset.yAxisID];
      ctx.fillStyle = fade(dataset.borderColor, 0.2);
      dataset.data.forEach((point) => {
        if (point.min == null || point.max == null) return;
        const left = x.getPixelForValue(point.x), right = x.getPixelForValue(point.x + point.resolution);
        ctx.fillRect(left, y.getPixelForValue(point.max), Math.max(1, right - left), Math.max(1, y.getPixelForValue(point.min) - y.getPixelForValue(point.max)));
      });
    });
    ctx.restore();
  }
};

export default React.memo(function MeasurementChart({ series, unit, title, compact = false, phases = EMPTY, activities = EMPTY, range, onRangeChange, onDetailRequest, revision }) {
  const theme = useTheme();
  const classes = useStyles();
  const { t } = useTranslation();
  const chartRef = useRef();
  const gesturing = useRef(false);
  const deferredDetail = useRef(null);
  const releaseGesture = useCallback(() => {
    gesturing.current = false;
    deferredDetail.current?.();
    deferredDetail.current = null;
  }, []);
  useEffect(() => {
    if (compact) return;
    // A click below the drag threshold has no plugin completion callback.
    // Release after its handlers finish, also when the pointer leaves the canvas.
    let timer;
    const release = () => { clearTimeout(timer); timer = setTimeout(releaseGesture, 0); };
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
    };
  }, [releaseGesture, compact]);
  const [expanded, setExpanded] = useState(false);
  const [viewport, setViewport] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailStatus, setDetailStatus] = useState('idle');
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const touch = useMediaQuery('(pointer: coarse)');
  const fullScreen = narrow || touch;
  const signature = series.map((item) => item.id).join('|') + ';' + activities.map((item) => item.id).join('|');
  const [start, end] = range && Number.isFinite(range.start) && Number.isFinite(range.end) ? [range.start, range.end] : [...series, ...activities].reduce((range, item) => item.points.length
    ? [Math.min(range[0], item.points[0].x), Math.max(range[1], (item.points[item.points.length - 1].last_at ?? item.points[item.points.length - 1].x))] : range, [Infinity, -Infinity]);
  const shownDetail = detail && detail.owner === onDetailRequest && detail.revision === revision && detail.signature === signature && viewport &&
    detail.from <= viewport.x.min && detail.to >= viewport.x.max ? detail.data : null;
  const shownSeries = useMemo(() => shownDetail ? series.map((item) => ({ ...item,
    points: shownDetail.series.find((s) => s.id === item.id)?.points ?? [] })) : series, [series, shownDetail]);
  const shownPhases = shownDetail?.phases ?? phases;
  const shownActivities = useMemo(() => shownDetail ? activities.map((item) => ({ ...item,
    points: shownDetail.activities.find((s) => s.id === item.id)?.points ?? [] })) : activities, [activities, shownDetail]);
  const visibleFrom = viewport?.x.min, visibleTo = viewport?.x.max;
  useEffect(() => {
    if (visibleFrom === undefined || !onDetailRequest || compact) { setDetail(null); setDetailStatus('idle'); return; }
    let active = true, cancel;
    setDetailStatus('loading');
    const from = visibleFrom, to = visibleTo;
    const timer = setTimeout(() => {
      const points = Math.max(60, Math.min(2000, Math.ceil(chartRef.current?.chartArea?.width ?? 600)));
      cancel = onDetailRequest({ from, to, points }, (response) => {
        if (!active) return;
        const apply = () => {
          if (!active) return;
          if (response.status === 'ok' && response.query_version === 1) {
            setDetail({ from, to, signature, revision, owner: onDetailRequest, data: response });
            setDetailStatus('ok');
          } else setDetailStatus(response.status === 'cancelled' ? 'idle' : 'unavailable');
        };
        if (gesturing.current) deferredDetail.current = apply;
        else apply();
      });
    }, 300);
    return () => { active = false; clearTimeout(timer); cancel?.(); deferredDetail.current = null; };
  }, [visibleFrom, visibleTo, onDetailRequest, revision, signature, compact, expanded]);
  const units = useMemo(() => [...new Set(series.map((s) => s.unit ?? unit ?? ''))], [series, unit]);
  const unitSignature = units.join('|');
  const data = useMemo(() => ({
    datasets: shownSeries.map((item, index) => {
      const color = getLightProfileColour(`${Math.floor(index / 6) % 4}:${index % 6}`);
      return {
        label: item.shortLabel ?? item.label, fullLabel: item.label, data: item.points, digits: item.digits ?? 2, unit: item.unit ?? unit ?? '',
        yAxisID: 'y' + units.indexOf(item.unit ?? unit ?? ''),
        borderColor: color, backgroundColor: color,
        borderWidth: item.main ? 2 : 1.5, borderDash: item.main ? [] : [4, 3],
        pointRadius: compact ? 0 : 1.5, pointHitRadius: 8, pointHoverRadius: 4,
        tension: 0, spanGaps: false, stepped: item.discrete ? 'after' : false
      };
    })
  }), [shownSeries, compact, unit, units]);
  useEffect(() => { setViewport(null); setDetail(null); gesturing.current = false; }, [start, end, unitSignature]);
  const rememberViewport = useCallback(({ chart }) => {
    setViewport(Object.fromEntries(Object.entries(chart.scales).map(([id, scale]) => [id, { min: scale.min, max: scale.max }])));
    releaseGesture();
  }, [releaseGesture]);
  const resetViewport = () => { gesturing.current = false; chartRef.current?.resetZoom(); setViewport(null); setDetail(null); };
  const extrema = useMemo(() => Object.fromEntries(units.map((name) => {
    const points = series.filter((item) => (item.unit ?? unit ?? '') === name).flatMap((item) => item.points);
    const low = points.reduce((value, point) => point.min == null ? value : Math.min(value, point.min), Infinity);
    const high = points.reduce((value, point) => point.max == null ? value : Math.max(value, point.max), -Infinity);
    return [name, { suggestedMin: low === Infinity ? undefined : low, suggestedMax: high === -Infinity ? undefined : high }];
  })), [series, units, unit]);
  const options = useMemo(() => ({
    responsive: true, maintainAspectRatio: false, parsing: false,
    // Chart.js 3 skips initializing new off-screen points with animation:false.
    // Zero duration keeps zoom-detail updates instant while preserving that
    // initialization (chartjs/Chart.js#10467). Hover/tooltips also stay instant.
    animation: { duration: 0 },
    transitions: { active: { animation: { duration: 0 } } },
    interaction: { intersect: false, mode: 'nearest' },
    layout: { autoPadding: false, padding: { top: 6, bottom: compact ? 0 : activities.length * 34 + (activities.length ? 6 : 0) } },
    scales: {
      x: { type: 'linear', display: !compact, ...(start < end ? { min: start, max: end } : {}), ...viewport?.x,
        grid: { display: false },
        ticks: { color: theme.palette.text.secondary, align: 'inner', maxTicksLimit: fullScreen ? 3 : 4, maxRotation: 0,
          font: { size: 11 }, padding: 2,
          callback: (value) => timeTick(value, viewport ? viewport.x.max - viewport.x.min : end - start) } },
      ...Object.fromEntries(units.map((name, index) => ['y' + index, {
        ...extrema[name],
        ...viewport?.['y' + index],
        type: 'linear', position: index === 0 ? 'left' : 'right', display: !compact,
        grid: { color: theme.palette.divider, drawOnChartArea: index === 0 },
        // Tick text changes during zoom; it must never change the plot width.
        afterFit: (scale) => { scale.width = compact ? 0 : fullScreen ? 48 : 52; },
        title: { display: false },
        ticks: { color: data.datasets.find((item) => item.unit === name)?.borderColor ?? theme.palette.text.secondary,
          font: { size: 11 }, padding: 3, maxTicksLimit: 5, callback: axisNumber }
      }]))
    },
    plugins: {
      zoom: historyZoomOptions(undefined, compact ? {} : {
        mode: touch ? 'x' : 'xy', pan: !touch || expanded, pinch: expanded, drag: !touch,
        onStart: () => { gesturing.current = true; }, onProgress: () => { gesturing.current = true; }, onComplete: rememberViewport,
        minRange: Math.min(1000, end - start), ...(start < end ? { bounds: { min: start, max: end } } : {})
      }),
      recordedPhases: { phases: shownPhases },
      recordedActivity: { activities: compact ? [] : shownActivities, color: theme.palette.text.secondary, divider: theme.palette.divider,
        phaseTextColors: irrigationPhaseColors.map((color) => theme.palette.getContrastText(color)) },
      legend: { display: !compact && data.datasets.length > 1,
        labels: { color: theme.palette.text.primary, usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
      tooltip: { intersect: true, animations: false, callbacks: {
        title: (items) => new Date(items[0].parsed.x).toLocaleString(),
        label: ({ dataset, parsed, raw }) => [
          dataset.fullLabel + ': ' + formatDatabaseNumber(parsed.y) + ' ' + dataset.unit,
          ...(raw.count != null ? [t('database_bucket_range', { min: formatDatabaseNumber(raw.min, dataset.digits), max: formatDatabaseNumber(raw.max, dataset.digits), count: raw.count })] : []),
          ...(raw.excluded ? [t('database_sensor_excluded')] : [])
        ]
      } }
    }
  }), [compact, extrema, start, end, theme, units, shownPhases, shownActivities, activities.length, t, data, viewport, expanded, rememberViewport, touch, fullScreen]);
  const knownPhases = [...new Set(phases.map((p) => p.y))].filter((phase) => phaseColors[phase]).sort();
  const irrigationPhases = [...new Set(activities.filter((activity) => activity.kind === 'irrigation_phase')
    .flatMap((activity) => activity.points.map((point) => point.y)))].filter((phase) => [0, 1, 2, 3].includes(phase)).sort();
  const controls = <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
    {units.map((name, index) => <Typography variant="caption" key={name}
      style={{ color: data.datasets.find((item) => item.unit === name)?.borderColor }}>
      {name}{units.length > 1 ? (index === 0 ? ' ←' : ' →') : ''}
    </Typography>)}
    <Tooltip title={t('database_reset_zoom')}><span><IconButton size="small" aria-label={t('database_reset_zoom')}
      disabled={!viewport} onClick={resetViewport}><RestoreIcon /></IconButton></span></Tooltip>
    {onRangeChange && <Button size="small" disabled={!viewport || (viewport.x.min === start && viewport.x.max === end)}
      onClick={() => onRangeChange(viewport.x.min, viewport.x.max)}>{t('database_use_chart_period')}</Button>}
    <InfoButton brief={t('database_zoom')} content={t('database_chart_zoom_help')} />
    <Tooltip title={t(expanded ? 'database_close_graph' : 'database_expand_graph')}>
      <IconButton size="small" aria-label={t(expanded ? 'database_close_graph' : 'database_expand_graph')}
        onClick={() => setExpanded(!expanded)}>{expanded ? <CloseIcon /> : <FullscreenIcon />}</IconButton>
    </Tooltip>
  </Box>;
  const detailInfo = <Box minHeight={20} display="flex" alignItems="center" style={{ gap: 6 }} aria-live="polite">
    {detailStatus === 'loading' && <CircularProgress size={12} aria-label={t('database_loading_detail')} />}
    <Typography variant="caption" color="textSecondary">
      {detailStatus === 'loading' ? t('database_loading_detail') : detailStatus === 'unavailable' ? t('database_detail_unavailable') :
        shownDetail ? t('database_resolution', { minutes: shownDetail.resolution_ms / 60000 }) : ''}
    </Typography>
  </Box>;
  const canvas = <div className={expanded ? classes.expandedCanvas : classes.canvas}
    style={{ height: compact ? 46 : expanded ? undefined : 260 + activities.length * 34,
      minHeight: expanded ? 260 + activities.length * 34 : undefined, position: 'relative', minWidth: 0 }}>
    <Line ref={chartRef} data={data} options={options} plugins={[background, envelope, activityLanes, historyTooltip, zoomPlugin]}
      style={{ touchAction: expanded ? 'none' : 'pan-y' }} role="img" aria-label={title} />
  </div>;
  return <>
    {!compact && <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
      {knownPhases.length ? knownPhases.map((phase) => <Typography key={phase} variant="caption" color="textSecondary">
        <span aria-hidden="true" style={{ display: 'inline-block', width: 9, height: 9, marginRight: 5, background: phaseColors[phase] }} />
        {t(dayNightCo2PreheatMapping[phase])}
      </Typography>) : <Typography variant="caption" color="textSecondary">{t('database_phase_missing')}</Typography>}
      <InfoButton brief={t('database_chart_context')} content={t('database_chart_context_help')} />
      {irrigationPhases.map((phase) => <Typography key={'irrigation-' + phase} variant="caption" color="textSecondary">
        <span aria-hidden="true" style={{ display: 'inline-block', width: 9, height: 9, marginRight: 5, background: irrigationPhaseColors[phase] }} />
        {'P' + phase + ' · ' + t(irrigationPhaseMapping[phase])}
      </Typography>)}
    </Box>}
    {!compact && !expanded && controls}
    {!compact && !expanded && detailInfo}
    {!expanded && canvas}
    <Dialog open={expanded} onClose={() => setExpanded(false)} fullWidth maxWidth="xl" fullScreen={fullScreen}
      PaperProps={{ 'aria-label': title, style: { height: fullScreen ? '100%' : '85vh' } }}>
      <DialogTitle disableTypography className={classes.dialogTitle}><Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <Typography variant="subtitle1">{title}</Typography>{controls}
      </Box></DialogTitle>
      <DialogContent className={classes.dialogContent}>{detailInfo}{expanded && canvas}</DialogContent>
    </Dialog>
  </>;
});
